import {
  GraphQLError,
  createApolloErrorTypeGuard,
} from '../errorHandling/GraphQLError';
import {
  QuerySalesTaxArgs,
  MutationCreateGoldPaidAccountArgs,
  MutationCreateBusinessClassPaidAccountArgs,
  MutationActivateGoldCreditArgs,
  MutationUpdateCreditCardArgs,
} from '../../generated';
import {
  prepareDataForApolloCache,
  QueryInfo,
} from '../prepareDataForApolloCache';
import { JSONObject } from '../types';
import { token } from '@trello/session-cookie';

export const salesTaxResolver = async (
  obj: object,
  args: QuerySalesTaxArgs,
  context: object,
  info: QueryInfo,
) => {
  const queryParams = new URLSearchParams();
  queryParams.set('country', args.country);
  queryParams.set('subtotal', `${args.subtotal}`);
  if (args.postalCode) {
    queryParams.set('postalCode', args.postalCode);
  }
  if (args.taxId) {
    queryParams.set('taxId', args.taxId);
  }

  const response = await fetch(
    `/1/members/me/paidAccount/salesTax?${queryParams.toString()}`,
  );

  if (!response.ok) {
    let errorCode = 'UNKNOWN_ERROR';
    try {
      const json = await response.json();
      errorCode = json.error;
    } catch (e) {
      // :disappear:
    }
    return prepareDataForApolloCache(
      {
        tax: 0,
        taxRate: 0,
        taxRegion: '',
        error: errorCode,
      },
      info,
    );
  }

  const tax = await response.json();
  return prepareDataForApolloCache(tax, info);
};

export interface CreatePaidAccountErrorExtension {
  name: 'CreatePaidAccountError';
  code:
    | 'FAILED_TO_CONFIRM'
    | 'USER_UNCONFIRMED'
    | 'UNKNOWN_ERROR'
    | 'LEGAL_INVALID_CONTACT_NAME'
    | 'LEGAL_INVALID_CONTACT_EMAIL'
    | 'LEGAL_INVALID_CONTACT_LOCALE'
    | 'LEGAL_INVALID_TOS'
    | 'PAYMENT_CARD_INVALID'
    | 'PAYMENT_CARD_DECLINED'
    | 'SHOP_INVALID_TAX_COUNTRY_CODE'
    | 'SHOP_INVALID_TAX_ZIP_CODE'
    | 'SHOP_INVALID_TAX_ID';
}

export const isCreatePaidAccountError = createApolloErrorTypeGuard<
  CreatePaidAccountErrorExtension
>('CreatePaidAccountError');

/**
 * Poll the entity endpoint requesting the paidAccount information until
 * the products you requested have been added to the `paidAccount.products`
 * property and the `paidAccount.standing` is among the expected account
 * states to confirm that the purchase has been completed.
 *
 * NOTE: Does not necessarily mean the purchase was successful. In certain
 * cases, it is possible to have a rejected payment, and still get a paid
 * account that is immediately marked as overdue -- but it does mean that
 * the purchase process is done
 */
export const confirmPaidAccount = (
  pollUrl: string,
  products: number[],
): Promise<JSONObject> =>
  new Promise((resolve, reject) => {
    let retries = 0;
    const checkPaidAccountStatus = async () => {
      if (retries >= 30) {
        return reject(
          new GraphQLError<CreatePaidAccountErrorExtension>(
            'Failed to confirm paid account after 30 seconds',
            {
              name: 'CreatePaidAccountError',
              code: 'FAILED_TO_CONFIRM',
            },
          ),
        );
      }
      const response = await fetch(pollUrl);
      if (response.ok) {
        const model = await response.json();
        const purchaseIsComplete =
          Array.isArray(model.paidAccount?.products) &&
          products.every((product) =>
            model.paidAccount.products.includes(product),
          ) &&
          [2, 3, 4, 5, 8, 9, 12].includes(model.paidAccount?.standing);
        if (purchaseIsComplete) {
          return resolve(model);
        }
      }
      retries++;
      setTimeout(checkPaidAccountStatus, 1000);
    };

    checkPaidAccountStatus();
  });

/**
 * The POST paidAccount API doesn't return JSON responses most of the time,
 * so we need to parse through the response text to try and figure out what went
 * wrong :sadpanda: :ughh: :tableflip:
 *
 * See unit tests for all observed server responses
 */
export const parseCreatePaidAccountError = (
  statusCode: number,
  errorText: string,
) => {
  let errorMessage = errorText;
  const extensions: CreatePaidAccountErrorExtension = {
    name: 'CreatePaidAccountError',
    code: 'UNKNOWN_ERROR',
  };

  try {
    const json = JSON.parse(errorText);
    const shopErrors = [
      'SHOP_INVALID_TAX_COUNTRY_CODE',
      'SHOP_INVALID_TAX_ZIP_CODE',
      'SHOP_INVALID_TAX_ID',
    ];
    errorMessage = json.message;
    extensions.code = shopErrors.includes(json.error)
      ? json.error
      : 'UNKNOWN_ERROR';
  } catch {
    const parsed = /^invalid value for (?<field>.*)$/.exec(errorText);
    switch (parsed?.groups?.field) {
      case 'country':
        extensions.code = 'SHOP_INVALID_TAX_COUNTRY_CODE';
        break;
      case 'zipCode':
        extensions.code = 'SHOP_INVALID_TAX_ZIP_CODE';
        break;
      case 'taxId':
        extensions.code = 'SHOP_INVALID_TAX_ID';
        break;
      case 'billingContactFullName':
        extensions.code = 'LEGAL_INVALID_CONTACT_NAME';
        break;
      case 'billingContactEmail':
        extensions.code = 'LEGAL_INVALID_CONTACT_EMAIL';
        break;
      case 'billingContactLocale':
        extensions.code = 'LEGAL_INVALID_CONTACT_LOCALE';
        break;
      case 'acceptTOS':
        extensions.code = 'LEGAL_INVALID_TOS';
        break;
      default:
    }
    if (errorText.includes('U.S. addresses require a 5 or 9 digit zip code')) {
      extensions.code = 'SHOP_INVALID_TAX_ZIP_CODE';
    }
    if (errorText.includes('must accept terms of service')) {
      extensions.code = 'LEGAL_INVALID_TOS';
    }
    if (errorText.includes('needs a credit card nonce')) {
      extensions.code = 'PAYMENT_CARD_INVALID';
    }
    if (errorText.includes('need to verify account')) {
      extensions.code = 'USER_UNCONFIRMED';
    }
    if (errorText.includes('card was declined')) {
      extensions.code = 'PAYMENT_CARD_DECLINED';
    }
    if (statusCode >= 500) {
      extensions.code = 'UNKNOWN_ERROR';
    }
  }

  return new GraphQLError<CreatePaidAccountErrorExtension>(
    errorMessage,
    extensions,
  );
};

interface CreatePaidAccountArgs {
  postUrl: string;
  pollUrl: string;
  products: number[];
  nonce: string;
  country: string;
  zipCode?: string;
  taxId?: string;
  acceptTOS: boolean;
  billingContactFullName: string;
  billingContactEmail: string;
  billingContactLocale: string;
}

/**
 * To create a paid account, you must first POST all the payment
 * details to the paidAccount endpoint under /organizations or /members.
 * This expects you to have already tokenized the credit card via
 * Braintree (you just pass in the nonce).
 *
 * The endpoint will validate all the details, but the mutation will
 * not immediately take effect. Once the POST succeeds, we then poll
 * the /organization or /member until the paidAccount object is updated.
 */
export const createPaidAccount = async (
  args: CreatePaidAccountArgs,
): Promise<JSONObject> => {
  const { postUrl, pollUrl, ...body } = args;
  const postResponse = await fetch(postUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ...body,
      // Server seems to error & get into an invalid state if
      // the products are sent in as numbers :noidea:
      // Classic implementation converts these to a string before
      // posting them, so we will too
      products: body.products.map((n) => n.toString()),
      token,
    }),
  });

  if (!postResponse.ok) {
    throw parseCreatePaidAccountError(
      postResponse.status,
      await postResponse.text(),
    );
  }

  return confirmPaidAccount(pollUrl, body.products);
};

export const createBusinessClassPaidAccount = async (
  obj: object,
  args: MutationCreateBusinessClassPaidAccountArgs,
  context: object,
  info: QueryInfo,
) => {
  const businessClassTeam = await createPaidAccount({
    postUrl: `/1/organizations/${args.idOrganization}/paidAccount`,
    pollUrl: `/1/organizations/${args.idOrganization}?paid_account=true`,
    products: args.products,
    nonce: args.nonce,
    country: args.country,
    zipCode: args.zipCode ?? '',
    taxId: args.taxId ?? '',
    acceptTOS: args.acceptTOS,
    billingContactFullName: args.name,
    billingContactEmail: args.email,
    billingContactLocale: args.locale,
  });

  return prepareDataForApolloCache(businessClassTeam, info);
};

export const createGoldPaidAccount = async (
  obj: object,
  args: MutationCreateGoldPaidAccountArgs,
  context: object,
  info: QueryInfo,
) => {
  const goldMember = await createPaidAccount({
    postUrl: `/1/members/${args.idMember}/paidAccount`,
    pollUrl: `/1/members/${args.idMember}?paid_account=true`,
    products: args.products,
    nonce: args.nonce,
    country: args.country,
    zipCode: args.zipCode ?? '',
    taxId: args.taxId ?? '',
    acceptTOS: args.acceptTOS,
    billingContactFullName: args.name,
    billingContactEmail: args.email,
    billingContactLocale: args.locale,
  });

  return prepareDataForApolloCache(goldMember, info);
};

export const activateGoldCredit = async (
  obj: object,
  args: MutationActivateGoldCreditArgs,
  context: object,
  info: QueryInfo,
) => {
  const postResponse = await fetch(
    `/1/members/${args.idMember}/unpaidAccount`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        // Server seems to error & get into an invalid state if
        // the products are sent in as numbers :noidea:
        // Classic implementation converts these to a string before
        // posting them, so we will too
        products: args.products.map((n) => n.toString()),
        token,
      }),
    },
  );

  if (!postResponse.ok) {
    throw parseCreatePaidAccountError(
      postResponse.status,
      await postResponse.text(),
    );
  }

  const goldMember = await confirmPaidAccount(
    `/1/members/${args.idMember}?paid_account=true`,
    args.products,
  );

  return prepareDataForApolloCache(goldMember, info);
};

/**
 * Update the Credit Card on file for a paid account
 */
export const updateCreditCard = async (
  obj: object,
  args: MutationUpdateCreditCardArgs,
  context: object,
  info: QueryInfo,
) => {
  const url = `/1/${args.accountType}/${args.accountId}/paidAccount/creditCard`;
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      nonce: args.nonce,
      country: args.country,
      zipCode: args.zipCode,
      taxId: args.taxId,
      token,
    }),
  });

  if (!response.ok) {
    throw parseCreatePaidAccountError(response.status, await response.text());
  }

  /**
   * Ok, this is a little nasty, but the prepareDataForApolloCache doesn't
   * support union types, so we have to manually wire up the typenames
   * based on the type of response we are expecting to get back (either a
   * member or an org)
   */
  const model = await response.json();
  if (args.accountType === 'organizations') {
    return {
      __typename: 'Organization',
      id: model.id,
      paidAccount: {
        __typename: 'PaidAccount',
        ...model.paidAccount,
        billingDates: JSON.stringify(model.paidAccount?.billingDates ?? {}),
        expirationDates: JSON.stringify(
          model.paidAccount?.expirationDates ?? {},
        ),
      },
    };
  } else {
    return {
      __typename: 'Member',
      id: model.id,
      paidAccount: {
        __typename: 'PaidAccount',
        ...model.paidAccount,
        billingDates: JSON.stringify(model.paidAccount?.billingDates ?? {}),
        expirationDates: JSON.stringify(
          model.paidAccount?.expirationDates ?? {},
        ),
      },
    };
  }
};
